// ---------------------------------------------------------------------------
// render-items — maps stored messages to the render-item array consumed by
// the virtualized conversation list.
//
// Extracted from main.tsx so the tool-call pairing logic can be unit tested.
// ---------------------------------------------------------------------------

import { extractSelectedSkills } from "@/lib/selected-skills";
import type { AgnoChatMessage } from "@/hooks/use-agno-chat";
import { hasBlockLevelMarkdown } from "@/lib/block-markdown";
import type { RunRequirementInfo } from "@/lib/stream-parser";
import {
  mergeLiveToolDetails,
  type ToolDetailSource,
} from "@/lib/tool-details";
import type { ThreadAnchorRef } from "@/lib/session";

/**
 * A file attachment carried on a render-item for display in a message bubble.
 *
 * ``url`` is either a browser blob: URL (just-attached, optimistic user
 * message), a data: URL (converted by PromptInput before submit), or an
 * HTTP(S) download URL (historical message loaded from the backend — the
 * server linkifier replaces workspace paths with signed ``/files/d/{jwt}``
 * links at read time, for both assistant messages and user
 * ``<uploaded_files>`` blocks).
 *
 * ``unshared``: ``true`` when the URL is *not* downloadable (the only case
 * in practice is a ``file://`` scheme that the server-side rewriter did
 * NOT rewrite — e.g. a transcript that pre-dates the rewriter or a
 * link to a file that failed validation). UI surfaces these as a chip
 * with an "(unshared)" badge and a non-clickable anchor tag so the user
 * is told the file exists but can't be downloaded by them.
 *
 * ``mediaType`` and ``filename`` come straight from the message part and are
 * used to pick an icon / thumbnail and display label.
 */
export interface RenderFilePart {
  filename?: string;
  mediaType?: string;
  url?: string;
  unshared?: boolean;
}

// ---------------------------------------------------------------------------
// <uploaded_files> tag parsing
//
// chat-bridge wraps uploaded file paths in an <uploaded_files> XML tag and
// appends it to the user message text.  At read time the server linkifies
// the paths inside the tag to signed Markdown download links:
//
//   <uploaded_files>
//   [Screenshot.png](https://…/files/d/{jwt})
//   </uploaded_files>
//   Use your file tools to read them as needed.
//
// This regex extracts the tag content so the UI can:
//   1. Parse the Markdown links into RenderFilePart[] for rendering as
//      attachment chips/thumbnails.
//   2. Strip the tag + trailing instruction from the displayed text.
// ---------------------------------------------------------------------------

const UPLOADED_FILES_RE =
  /<uploaded_files>\s*([\s\S]*?)\s*<\/uploaded_files>\s*(?:Use your file tools to read them as needed\.?)?/g;

// ---------------------------------------------------------------------------
// <voice_transcript> tag parsing
//
// chat-bridge injects the transcribed text of a voice note at the top of
// the user message inside a <voice_transcript> XML tag:
//
//   <voice_transcript>
//   Hello, can you summarize the meeting notes from yesterday?
//   </voice_transcript>
//
// This regex extracts the transcript text so the UI can:
//   1. Render it in a styled "voice note transcript" card with a mic icon
//      (instead of raw XML-tagged text in the message bubble).
//   2. Strip the tag from the displayed message text.
// ---------------------------------------------------------------------------

const VOICE_TRANSCRIPT_RE =
  /<voice_transcript>\s*([\s\S]*?)\s*<\/voice_transcript>/;

// ---------------------------------------------------------------------------
// <user_meta> tag parsing
//
// chat-bridge prepends a self-closing <user_meta> XML tag to every web user
// message before forwarding it to agno-server:
//
//   <user_meta name="Alice" timestamp="2026-07-17 14:30 UTC" elapsed_since_last_msg="5m 30s" locale="de" locale_name="Deutsch" />
//   Hello, can you help me?
//
// When present, locale + locale_name identify the user's selected UI language
// and tell agents to respond in that language. Both are omitted when unknown.
//
// This regex extracts the tag so the UI can:
//   1. Strip the tag from the displayed message text (it should not appear
//      inline in the message bubble).
//   2. Parse the attributes into a structured UserMeta object for display
//      behind a hover icon on the message.
//
// The tag is self-closing and always appears at the very start of the message
// text, before any user-typed content, voice transcript tags, or uploaded
// files blocks.  It is only present on web user messages (Discord/Teams
// messages and assistant messages never have it).
// ---------------------------------------------------------------------------

const USER_META_RE = /<user_meta\s+([^]*?)\/>/;

// ---------------------------------------------------------------------------
// <task_status_event> tag parsing
//
// agno-server prepends this self-closing tag to the prompt used to trigger an
// agent after a task status transition. The following human-readable prompt
// is for the agent; the UI presents it in a system-event row instead of as a
// user-authored message bubble.
// ---------------------------------------------------------------------------

const TASK_STATUS_EVENT_RE = /<task_status_event\s+([^]*?)\/>/;

// ---------------------------------------------------------------------------
// <subagent_completion_event> tag parsing
//
// agno-server prepends this self-closing tag to a server-originated parent
// continuation when an opted-in asynchronous sub-agent reaches a terminal
// state. It is deliberately distinct from <task_status_event>: no project
// association is implied by a child-agent completion.
// ---------------------------------------------------------------------------

// Some older server builds used underscores while the public event name was
// documented with hyphens. Accept both spellings during mixed-version
// rollouts; they carry the exact same legacy attribute contract.
const SUBAGENT_COMPLETION_EVENT_RE = /<subagent[-_]completion[-_]event\s+([^]*?)\/>/;

// ---------------------------------------------------------------------------
// <task-completion-events> batch parsing
//
// Newer agno-server versions persist one server-originated batch per wake.
// Keep this separate from the legacy single-subagent tag above: mixed server
// versions are expected during a rolling UI/server upgrade.
// ---------------------------------------------------------------------------

const TASK_COMPLETION_EVENTS_RE =
  /<task-completion-events\b[^>]*>([\s\S]*?)<\/task-completion-events>/;
const TASK_COMPLETION_EVENTS_OPEN_RE = /<task-completion-events\b[^>]*>/;
const TASK_COMPLETION_EVENT_RE = /<task-completion-event\s+([^]*?)\/>/g;

const MARKDOWN_LINK_RE = /\[([^\]]*)\]\(([^)]+)\)/g;

// A `file://` Markdown link inside message content. Matches the URL portion
// of `[label](file:///…)` so we can mark the file as "unshared" (the server
// didn't rewrite it; we can't just hand the browser a file:// URI and expect
// it to load — most browsers won't, and chat-bridge's reverse proxy can't
// reach local files anyway).
//
// Captures the entire `[label](file://…)` body verbatim. Anchor: `file://`
// must be exact lowercase (the URI scheme is case-insensitive but every
// well-known consumer emits lowercase). The label group is `*?`/non-greedy
// to allow empty labels (`[](file:///…)`), which we then fall back to
// basename for.
const FILE_URI_LINK_RE =
  /\[([^\]]*)\]\((file:\/\/[^)\s]+?)\)/g;
// Old servers can return portable workspace links unchanged. Bare tilde
// destinations are also treated as unshared instead of browser-relative URLs.
const TILDE_LINK_RE =
  /\[([^\]]*)\]\((~[^)\s]*)\)/g;

// A signed file-download Markdown link produced by the server-side
// FileLinkService at read time.  The URL path always contains ``/files/d/``
// followed by a JWT token.  We match the full ``[label](https://…/files/d/{jwt})``
// link so we can extract it as a ``RenderFilePart`` for rendering as an
// attachment pill below the message text (in addition to the in-text link
// that Streamdown renders).
//
// The path pattern ``/files/d/`` is the canonical download route defined by
// chat-bridge (``app.get("/files/d/*")``) and the FileLinkService
// (``f"{base_url}/files/d/{token}"``).  The JWT body uses URL-safe base64
// characters: ``[A-Za-z0-9_.-]+`` plus ``=`` padding.
const SHARED_FILE_LINK_RE =
  /\[([^\]]*)\]\((https?:\/\/[^)]*\/files\/d\/[A-Za-z0-9_.\-=]+)\)/g;

/**
 * Extract signed file-download Markdown links from assistant message text.
 *
 * The server-side ``FileLinkService.rewrite_file_uri_links`` replaces
 * ``[label](file:///absolute/path)`` links with signed
 * ``[label](https://…/files/d/{jwt})`` links at read time.  This function
 * scans for those signed links and returns them as ``RenderFilePart`` objects
 * (with ``unshared`` left undefined — these are downloadable) so the UI can
 * render them as attachment pills *below* the message text, in addition to
 * the in-text Markdown link that ``Streamdown`` renders inline.
 *
 * The in-text link is deliberately **not** stripped — the user sees the file
 * both as a clickable link in the prose and as a pill below it.  This is the
 * "dual representation" described in the file-link UX design.
 *
 * De-duplicates by URL so the same file referenced twice in a message shows
 * as ONE pill.
 *
 * Only matches the ``/files/d/{jwt}`` download-route pattern — arbitrary
 * ``https://`` links (e.g. to external websites) are ignored.
 */
export function extractSharedFileLinks(text: string): RenderFilePart[] {
  const seen = new Set<string>();
  const out: RenderFilePart[] = [];
  if (!text) return out;
  const re = new RegExp(SHARED_FILE_LINK_RE.source, "g");
  let m: RegExpExecArray | null;
  while ((m = re.exec(text)) !== null) {
    const label = m[1]?.trim() || undefined;
    const url = m[2];
    if (seen.has(url)) continue;
    seen.add(url);
    const cleaned = label ? stripMarkdownSyntax(label) : undefined;
    out.push({
      filename: cleaned || basename(url),
      url,
    });
  }
  return out;
}

/**
 * Extract unshared ``file://`` and ``~`` Markdown links from message text.
 *
 * The server-side rewriter replaces ``[label](file:///absolute/path)`` with
 * a signed ``[label](https://…/files/d/{jwt})`` link at read time. Defensively,
 * any ``file://`` link that *does* slip through (a transcript that pre-dates
 * the rewriter, or a rewriter failure where the silent-drop path explicitly
 * leaves the link intact) is surfaced here so the UI can render an
 * "unshared" chip instead of a clickable broken link.
 *
 * Each result is a :type:`RenderFilePart` with ``unshared: true`` and the
 * raw ``file://…`` URL preserved on ``url`` (so a curious developer can still
 * see what path was meant). The label of the original Markdown link is
 * preferred over the path basename for display.
 *
 * De-duplicates by URL — the same ``file://`` link in two places in the
 * transcript shows as ONE chip, not two.
 */
export function extractUnsharedFileLinks(text: string): RenderFilePart[] {
  const seen = new Set<string>();
  const out: RenderFilePart[] = [];
  if (!text) return out;
  for (const pattern of [FILE_URI_LINK_RE, TILDE_LINK_RE]) {
    const re = new RegExp(pattern.source, "g");
    let m: RegExpExecArray | null;
    while ((m = re.exec(text)) !== null) {
      const label = m[1]?.trim() || undefined;
      const url = m[2];
      if (seen.has(url)) continue;
      seen.add(url);
      const cleaned = label ? stripMarkdownSyntax(label) : undefined;
      out.push({
        filename: cleaned || basename(url),
        url,
        unshared: true,
      });
    }
  }
  return out;
}

/**
 * Extract file attachments from ``<uploaded_files>`` tags in a user message.
 *
 * After server-side linkification, paths inside the tag are Markdown links
 * ``[filename](https://…/files/d/{jwt})``.  This parses them into
 * ``RenderFilePart`` objects.  Paths that were NOT linkified (file no
 * longer exists) remain as plain text lines and are extracted as file parts
 * with ``url`` undefined.
 *
 * Returns ``{ files, cleanText }`` where ``cleanText`` is the original text
 * with the ``<uploaded_files>`` block and trailing instruction removed.
 */
export function extractUploadedFiles(text: string): {
  files: RenderFilePart[];
  cleanText: string;
} {
  const files: RenderFilePart[] = [];

  // Strip the tag + trailing instruction from display text.
  const cleanText = text.replace(UPLOADED_FILES_RE, "").trim();

  // Extract file parts from each <uploaded_files> block.
  let blockMatch: RegExpExecArray | null;
  const blockRe = new RegExp(UPLOADED_FILES_RE.source, "g");
  while ((blockMatch = blockRe.exec(text)) !== null) {
    const inner = blockMatch[1];

    // Parse Markdown links → RenderFilePart with url.
    const linkRe = new RegExp(MARKDOWN_LINK_RE.source, "g");
    let linkMatch: RegExpExecArray | null;
    let lastIndex = 0;
    while ((linkMatch = linkRe.exec(inner)) !== null) {
      // Any text before this link (non-linkified paths) → file part without url.
      if (linkMatch.index > lastIndex) {
        const between = inner.slice(lastIndex, linkMatch.index).trim();
        if (between) {
          for (const line of between.split("\n")) {
            const trimmed = line.trim().replace(/^[-•]\s*/, "");
            if (trimmed) {
              files.push({ filename: basename(stripMarkdownSyntax(trimmed)) });
            }
          }
        }
      }
      const url = linkMatch[2];
      if (url.startsWith("file://")) {
        // Defensive: a `file://` Markdown link inside `<uploaded_files>`.
        // The server-side linkifier should have rewritten it; if it slipped
        // through (test environment, mismatched platform, etc.) treat it
        // as unshared rather than a clickable broken link.
        files.push({
          filename: stripMarkdownSyntax(linkMatch[1]) || basename(url),
          url,
          unshared: true,
        });
      } else {
        files.push({
          filename: stripMarkdownSyntax(linkMatch[1]),
          url,
        });
      }
      lastIndex = linkRe.lastIndex;
    }

    // Trailing text after last link (non-linkified paths).
    if (lastIndex < inner.length) {
      const trailing = inner.slice(lastIndex).trim();
      if (trailing) {
        for (const line of trailing.split("\n")) {
          const trimmed = line.trim().replace(/^[-•]\s*/, "");
          if (trimmed) {
            files.push({ filename: basename(stripMarkdownSyntax(trimmed)) });
          }
        }
      }
    }
  }

  return { files, cleanText };
}

/**
 * Extract a ``<user_meta>`` tag from a user message.
 *
 * chat-bridge prepends the self-closing tag at the very start of web user
 * messages.  This function extracts the tag's attributes into a structured
 * ``UserMeta`` object and strips the tag from the displayed message text so
 * the UI can display the metadata behind a hover icon instead of as raw
 * XML inline.
 *
 * Returns ``{ userMeta, cleanText }`` where ``userMeta`` is the parsed
 * metadata (or ``null`` if no tag is present or the tag is malformed —
 * missing the required ``timestamp`` attribute) and ``cleanText`` is the
 * original text with the tag removed.
 */
export function extractUserMeta(text: string): {
  userMeta: UserMeta | null;
  cleanText: string;
} {
  const match = USER_META_RE.exec(text);
  if (!match) {
    return { userMeta: null, cleanText: text };
  }
  const attrString = match[1];
  const get = (attr: string): string | undefined => {
    const re = new RegExp(`\\b${attr}="([^"]*)"`);
    const m = re.exec(attrString);
    return m ? m[1] : undefined;
  };
  const timestamp = get("timestamp");
  if (!timestamp) {
    // Malformed tag — missing the required timestamp attribute.  Return
    // null so the UI doesn't display a hover icon with incomplete data.
    // The tag is still stripped from the display text.
    return { userMeta: null, cleanText: text.replace(USER_META_RE, "").trim() };
  }
  const userMeta: UserMeta = {
    name: get("name"),
    timestamp,
    elapsedSinceLastMsg: get("elapsed_since_last_msg"),
  };
  const locale = get("locale");
  const localeName = get("locale_name");
  if (locale) userMeta.locale = locale;
  if (localeName) userMeta.localeName = localeName;
  const cleanText = text.replace(USER_META_RE, "").trim();
  return { userMeta, cleanText };
}

/** Decode the XML entities emitted by the Python task-status event builder. */
function unescapeXmlAttribute(value: string): string {
  return value
    .replace(/&quot;/g, '"')
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&amp;/g, "&");
}

/**
 * Extract a ``<task_status_event>`` tag from an agent-trigger message.
 *
 * Returns ``{ taskStatusEvent, cleanText }`` where ``taskStatusEvent`` is
 * null for ordinary or malformed messages. A matching tag is always removed
 * from ``cleanText`` so malformed metadata never leaks into a message bubble.
 */
export function extractTaskStatusEvent(text: string): {
  taskStatusEvent: TaskStatusEvent | null;
  cleanText: string;
} {
  const match = TASK_STATUS_EVENT_RE.exec(text);
  if (!match) {
    return { taskStatusEvent: null, cleanText: text };
  }

  const attrString = match[1];
  const get = (attr: string): string | undefined => {
    const re = new RegExp(`\\b${attr}="([^"]*)"`);
    const value = re.exec(attrString)?.[1];
    return value === undefined ? undefined : unescapeXmlAttribute(value);
  };
  const taskId = get("task_id");
  const projectId = get("project_id");
  const oldStatus = get("old_status");
  const newStatus = get("new_status");
  const timestamp = get("timestamp");
  const cleanText = text.replace(TASK_STATUS_EVENT_RE, "").trim();

  if (!taskId || !projectId || !oldStatus || !newStatus || !timestamp) {
    return { taskStatusEvent: null, cleanText };
  }

  return {
    taskStatusEvent: {
      taskId,
      projectId,
      oldStatus,
      newStatus,
      changedBy: get("changed_by"),
      timestamp,
    },
    cleanText,
  };
}

/** Extract a ``<subagent_completion_event>`` system continuation tag. */
export function extractSubagentCompletionEvent(text: string): {
  subagentCompletionEvent: SubagentCompletionEvent | null;
  cleanText: string;
} {
  const match = SUBAGENT_COMPLETION_EVENT_RE.exec(text);
  if (!match) {
    return { subagentCompletionEvent: null, cleanText: text };
  }

  const attrString = match[1];
  const get = (attr: string): string | undefined => {
    const re = new RegExp(`\\b${attr}="([^"]*)"`);
    const value = re.exec(attrString)?.[1];
    return value === undefined ? undefined : unescapeXmlAttribute(value);
  };
  const taskId = get("task_id");
  const terminalStatus = get("terminal_status");
  const childSessionId = get("child_session_id");
  const cleanText = text.replace(SUBAGENT_COMPLETION_EVENT_RE, "").trim();
  const validTerminalStatuses = new Set([
    "completed",
    "failed",
    "cancelled",
    "timed_out",
  ]);

  if (
    !taskId
    || !childSessionId
    || !terminalStatus
    || !validTerminalStatuses.has(terminalStatus)
  ) {
    return { subagentCompletionEvent: null, cleanText };
  }

  return {
    subagentCompletionEvent: {
      taskId,
      terminalStatus: terminalStatus as SubagentCompletionEvent["terminalStatus"],
      childSessionId,
    },
    cleanText,
  };
}

/** Extract a batched task-completion-events continuation block. */
export function extractTaskCompletionEvents(text: string): {
  taskCompletionEvents: TaskCompletionEvents | null;
  cleanText: string;
} {
  const match = TASK_COMPLETION_EVENTS_RE.exec(text);
  if (!match) {
    // A truncated system message should never become an empty or broken
    // bubble. The reserved opening tag is enough to show the generic safe
    // fallback even when an older/newer server produced malformed content.
    if (TASK_COMPLETION_EVENTS_OPEN_RE.test(text)) {
      return {
        taskCompletionEvents: { events: [], isFallback: true },
        cleanText: text.replace(TASK_COMPLETION_EVENTS_OPEN_RE, "").trim(),
      };
    }
    return { taskCompletionEvents: null, cleanText: text };
  }

  const cleanText = text.replace(TASK_COMPLETION_EVENTS_RE, "").trim();
  const validTerminalStatuses = new Set([
    "completed",
    "failed",
    "cancelled",
    "timed_out",
  ]);
  const knownTaskBackends = new Set(["subagent", "shell", "workflow"]);
  const events: TaskCompletionEvent[] = [];
  let hasMalformedEntry = false;
  for (const entry of match[1].matchAll(TASK_COMPLETION_EVENT_RE)) {
    const attrString = entry[1];
    const get = (attr: string): string | undefined => {
      const re = new RegExp("\\b" + attr + '="([^"]*)"');
      const value = re.exec(attrString)?.[1];
      return value === undefined ? undefined : unescapeXmlAttribute(value);
    };
    const kind = get("kind") || "task_transition";
    const taskId = get("task_id");
    const status = get("status");
    const backend = get("backend");
    const pointerTool = get("pointer_tool");
    if (
      kind !== "task_transition"
      || !taskId
      || !status
      || !backend
      || !pointerTool
      || !validTerminalStatuses.has(status)
      || !knownTaskBackends.has(backend)
    ) {
      hasMalformedEntry = true;
      continue;
    }
    events.push({
      taskId,
      kind,
      backend,
      status: status as TaskCompletionEvent["status"],
      pointerTool,
      exitCode: get("exit_code"),
      description: get("description"),
      resultSummary: get("result_summary"),
    });
  }

  // Newer kinds intentionally share this envelope. Until the UI has a
  // dedicated renderer for one, keep the batch visible as a safe generic
  // fallback instead of silently implying every entry was understood.
  const hasUnknownEntry = /<(?!task-completion-event\b)[A-Za-z][\w:-]*\b[^>]*\/?\s*>/.test(
    match[1],
  );

  // A malformed or unknown/future entry still gets a clear system row. Known
  // task entries remain available for the compact list below.
  return {
    taskCompletionEvents: {
      events,
      isFallback: events.length === 0 || hasMalformedEntry || hasUnknownEntry,
    },
    cleanText,
  };
}

/**
 * Extract a voice-note transcript from a ``<voice_transcript>`` tag in a
 * user message.
 *
 * chat-bridge injects the transcript at the top of the user message text.
 * This function extracts the transcript text and strips the tag from the
 * displayed message text so the UI can render the transcript in a styled
 * card instead of raw XML-tagged text.
 *
 * Returns ``{ transcript, cleanText }`` where ``transcript`` is the
 * extracted text (or ``null`` if no tag is present) and ``cleanText`` is
 * the original text with the tag removed.
 */
export function extractVoiceTranscript(text: string): {
  transcript: string | null;
  cleanText: string;
} {
  const match = VOICE_TRANSCRIPT_RE.exec(text);
  if (!match) {
    return { transcript: null, cleanText: text };
  }
  const transcript = match[1].trim();
  const cleanText = text.replace(VOICE_TRANSCRIPT_RE, "").trim();
  return { transcript, cleanText };
}

/** Extract the basename from a path (cross-platform). */
function basename(path: string): string {
  const parts = path.replace(/\\/g, "/").split("/");
  return parts[parts.length - 1] || path;
}

/**
 * Strip inline Markdown formatting from a filename label.
 *
 * Agents sometimes write `` [`somecode.py`](url) `` instead of plain
 * ``[somecode.py](url)``. The backticks — and other inline Markdown
 * wrappers like ``*`` (italic/​bold) — end up in the file pill label if
 * not removed here.
 *
 * Only backticks and asterisks are stripped. Underscores are **deliberately
 * left intact** because they are extremely common in real filenames
 * (``my_file.py``, ``test_data.csv``) and stripping them would corrupt the
 * displayed name. A Markdown-aware parser could distinguish ``_italic_``
 * from ``my_file``, but that's overkill for a single filename label.
 */
function stripMarkdownSyntax(label: string): string {
  return label
    .replace(/`+/g, "") // code spans: `code` → code
    .replace(/\*+/g, "") // bold/italic: **bold**, *italic*
    .trim();
}

/**
 * Structured metadata extracted from a ``<user_meta>`` tag prepended by
 * chat-bridge to web user messages.  The UI displays this behind a hover
 * icon on the message bubble instead of rendering the raw XML inline.
 *
 * ``timestamp`` is always present per the tag spec. ``name``,
 * ``elapsedSinceLastMsg``, and the paired locale fields are optional.
 */
export interface UserMeta {
  name?: string;
  timestamp: string;
  elapsedSinceLastMsg?: string;
  /** Short code from the user's selected UI locale, e.g. ``de``. */
  locale?: string;
  /** Canonical native language name from ``locale_name``, e.g. ``Deutsch``. */
  localeName?: string;
}

/** Structured details extracted from a task-status event trigger prompt. */
export interface TaskStatusEvent {
  taskId: string;
  projectId: string;
  oldStatus: string;
  newStatus: string;
  changedBy?: string;
  timestamp: string;
}

/** Structured details extracted from an async sub-agent completion wake. */
export interface SubagentCompletionEvent {
  taskId: string;
  terminalStatus: "completed" | "failed" | "cancelled" | "timed_out";
  childSessionId: string;
}

/** One known task-transition entry in a generic session wake batch. */
export interface TaskCompletionEvent {
  taskId: string;
  kind: string;
  backend: string;
  status: "completed" | "failed" | "cancelled" | "timed_out";
  pointerTool: string;
  exitCode?: string;
  description?: string;
  resultSummary?: string;
}

/** Parsed batch, including a safe fallback for future/unknown event kinds. */
export interface TaskCompletionEvents {
  events: TaskCompletionEvent[];
  isFallback: boolean;
}

export type ThreadGroupItem = {
  kind: "thread-group";
  id: string;
  sourceMessageId: string;
  threads: Array<{
    childSessionId: string;
    sourceAnchorKind: "message" | "block";
    sourceBlockIndex: number | null;
    threadTitle: string | null;
  }>;
};

export type RenderItem =
  | {
      /** A task-status-change trigger, rendered as a system event. */
      kind: "task-status-event";
      id: string;
      event: TaskStatusEvent;
      /** Agent-facing status-change instruction following the XML tag. */
      text: string;
    }
  | {
      /** A server-originated async sub-agent completion continuation. */
      kind: "subagent-completion-event";
      id: string;
      event: SubagentCompletionEvent;
      /** Agent-facing continuation instruction following the XML tag. */
      text: string;
    }
  | {
      /** A server-originated generic batch of task completion events. */
      kind: "task-completion-events";
      id: string;
      event: TaskCompletionEvents;
      /** Agent-facing continuation instruction following the XML tag. */
      text: string;
    }
  | {
      kind: "message";
      id: string;
      role: "user" | "assistant";
      text: string;
      /** Stable optimistic boundary id retained after DB reconciliation. */
      toolGroupBoundaryId?: string;
      /** File attachments on this message (empty array when none). */
      files: RenderFilePart[];
      /**
       * Voice-note transcript extracted from a ``<voice_transcript>`` tag
       * in the user message.  When present, the UI renders it in a styled
       * card with a mic icon above the message text.  ``null`` for
       * assistant messages or user messages without a voice note.
       */
      voiceTranscript: string | null;
      selectedSkills: string[];
      /**
       * Structured metadata extracted from a ``<user_meta>`` tag
       * prepended by chat-bridge to web user messages.  When present,
       * the UI displays it behind a hover icon on the message bubble.
       * ``null`` for assistant messages, Discord/Teams messages, older
       * messages (pre-wrapper), or optimistic local messages before DB
       * sync.
       */
      userMeta: UserMeta | null;
      scheduledRun?: { display_name: string | null; cron: string | null; timezone: string | null; firedAt: string };
      /** True only when this assistant turn was actually served by flash. */
      flashMode: boolean;
      composerAction?: { label: string; taskId: string; actionId?: string };
    }
  | {
      kind: "tool";
      id: string;
      name: string;
      status: "start" | "end" | "error";
      source: ToolDetailSource;
      workflowTaskId?: unknown;
    }
  | { kind: "thinking"; id: "__thinking__" }
  | { kind: "resuming"; id: "__resuming__" }
  | {
      kind: "hitl";
      id: string;
      runId: string;
      requirements: RunRequirementInfo[];
      resolved: boolean;
      recoveryError?: string;
    }
  | {
      kind: "compaction";
      id: string;
      tokensBefore: number;
      summaryLength: number;
    }
  | {
      kind: "tool-group";
      id: string;
      /**
       * The compacted sequence. Intermediate assistant narration stays inside
       * the disclosure; final assistant text and block-level Markdown are
       * emitted as visible messages.
       */
      items: RenderItem[];
      toolCount: number;
      isStreaming: boolean;
      lastToolName: string;
      lastToolStatus: "start" | "end" | "error";
    }
  | ThreadGroupItem;

/**
 * Map the DB ``status`` column value to the badge status used by the UI.
 *
 * - ``in_progress`` → ``start`` (spinning badge)
 * - ``error``       → ``error`` (red badge)
 * - anything else   → ``end``   (green badge — covers ``complete``)
 */
function dbStatusToBadgeStatus(raw: unknown): "start" | "end" | "error" {
  if (raw === "error") return "error";
  if (raw === "in_progress") return "start";
  return "end";
}

/**
 * Per-message cache for the `{ kind: "message" }` render item.
 *
 * Keyed by the message object identity. `mergeMessages` preserves the object
 * reference for rows that did not change render-relevant fields, so during
 * streaming/polling only the actively-growing assistant row (and genuinely
 * changed rows) miss the cache. This avoids re-running the `<uploaded_files>`
 * regex extraction over every historical user message on every token/tick.
 *
 * A WeakMap means entries are GC'd automatically once a message object is no
 * longer referenced by the store.
 */
const messageItemCache = new WeakMap<AgnoChatMessage, RenderItem>();

/**
 * Convert a single non-tool message to its render item, memoized by object
 * identity. Pure — depends only on the message itself.
 */
function messageToRenderItem(msg: AgnoChatMessage): RenderItem {
  const cached = messageItemCache.get(msg);
  if (cached) return cached;

  const text =
    msg.parts
      ?.filter((p: any) => p.type === "text")
      .map((p: any) => p.text)
      .join("") || msg.content || "";

  // Extract file parts for rendering as attachment chips / thumbnails.
  //
  // Two sources of file parts:
  //   1. Optimistic user message — file parts from the chat-SDK message
  //      (blob/data URLs).  These are present only before the DB row
  //      reconciles the optimistic message.
  //   2. Historical user messages — the backend persists the user message
  //      text (which includes the <uploaded_files> block appended by
  //      chat-bridge and linkified by the server) as `content`.  We parse
  //      the <uploaded_files> tag to extract file parts for rendering as
  //      attachment chips/thumbnails, and strip the tag from display text.
  const partFiles: RenderFilePart[] =
    msg.parts
      ?.filter((p: any) => p.type === "file")
      .map((p: any) => ({
        filename: p.filename,
        mediaType: p.mediaType,
        url: p.url,
      })) ?? [];

  const taskStatusExtracted = msg.role === "user"
    ? extractTaskStatusEvent(text)
    : { taskStatusEvent: null, cleanText: text };
  if (taskStatusExtracted.taskStatusEvent) {
    const item: RenderItem = {
      kind: "task-status-event",
      id: msg.id,
      event: taskStatusExtracted.taskStatusEvent,
      text: taskStatusExtracted.cleanText,
    };
    messageItemCache.set(msg, item);
    return item;
  }

  const taskCompletionEventsExtracted = msg.role === "user"
    ? extractTaskCompletionEvents(taskStatusExtracted.cleanText)
    : { taskCompletionEvents: null, cleanText: taskStatusExtracted.cleanText };
  if (taskCompletionEventsExtracted.taskCompletionEvents) {
    const item: RenderItem = {
      kind: "task-completion-events",
      id: msg.id,
      event: taskCompletionEventsExtracted.taskCompletionEvents,
      text: taskCompletionEventsExtracted.cleanText,
    };
    messageItemCache.set(msg, item);
    return item;
  }

  const subagentCompletionExtracted = msg.role === "user"
    ? extractSubagentCompletionEvent(taskCompletionEventsExtracted.cleanText)
    : { subagentCompletionEvent: null, cleanText: taskCompletionEventsExtracted.cleanText };
  if (subagentCompletionExtracted.subagentCompletionEvent) {
    const item: RenderItem = {
      kind: "subagent-completion-event",
      id: msg.id,
      event: subagentCompletionExtracted.subagentCompletionEvent,
      text: subagentCompletionExtracted.cleanText,
    };
    messageItemCache.set(msg, item);
    return item;
  }

  // Older servers emit a single legacy tag. If that tag is malformed, still
  // show a visible, generic system row rather than returning an empty user
  // bubble (or exposing raw transport markup).
  if (
    msg.role === "user"
    && SUBAGENT_COMPLETION_EVENT_RE.test(
      taskCompletionEventsExtracted.cleanText,
    )
  ) {
    const item: RenderItem = {
      kind: "task-completion-events",
      id: msg.id,
      event: { events: [], isFallback: true },
      text: subagentCompletionExtracted.cleanText,
    };
    messageItemCache.set(msg, item);
    return item;
  }

  let displayText = subagentCompletionExtracted.cleanText;
  let tagFiles: RenderFilePart[] = [];
  let unsharedFiles: RenderFilePart[] = [];
  let sharedFiles: RenderFilePart[] = [];
  let voiceTranscript: string | null = null;
  let selectedSkills: string[] = [];
  let userMeta: UserMeta | null = null;

  // Extract <user_meta> tag first (it's at the very top of the message,
  // before <voice_transcript> and <uploaded_files>).  The tag is only
  // present on web user messages (chat-bridge prepends it in bot.ts when
  // thread.id.startsWith("web:")).  Running this for all message roles is
  // harmless — assistant and Discord/Teams messages simply have no tag,
  // so extractUserMeta returns { userMeta: null, cleanText: text }.
  const metaExtracted = extractUserMeta(displayText);
  userMeta = metaExtracted.userMeta;
  displayText = metaExtracted.cleanText;

  if (msg.role === "user") {
    const skillExtracted = extractSelectedSkills(displayText);
    selectedSkills = skillExtracted.skills;
    displayText = skillExtracted.cleanText;
    // Extract voice transcript (it's at the top of the message, after
    // the <user_meta> tag which was already stripped above).
    // The tag is stripped from displayText before extractUploadedFiles
    // runs, so the two extractions don't interfere.
    //
    // The tag is present in canonical (DB-backed) messages because
    // chat-bridge injects it server-side in runAgnoStream().  However,
    // the optimistic SSE message (constructed in sendMessage before the
    // POST to /chat/web) does NOT contain the tag — chat-bridge adds it
    // only when forwarding the message to the agno-server.  To bridge
    // this gap, sendMessage stores the transcript in
    // ``metadata.voiceTranscript`` on the optimistic message.  We fall
    // back to that metadata value when the tag is not found in the text
    // so the VoiceTranscriptCard renders immediately during streaming,
    // before the canonical DB row arrives.
    const voiceExtracted = extractVoiceTranscript(displayText);
    voiceTranscript = voiceExtracted.transcript;
    if (voiceTranscript === null && msg.metadata?.voiceTranscript) {
      voiceTranscript = String(msg.metadata.voiceTranscript);
    }
    displayText = voiceExtracted.cleanText;

    const extracted = extractUploadedFiles(displayText);
    tagFiles = extracted.files;
    displayText = extracted.cleanText;
    // `file://` Markdown links inside `<uploaded_files>` were already
    // marked unshared by extractUploadedFiles; nothing extra to do.
  } else {
    // Assistant messages: scan the rendered text for any `file://`
    // Markdown links as a defence-in-depth against server-side
    // rewrites that didn't fire (older transcripts, rewriter
    // blow-up on a per-message basis). The server's plan is to
    // silently drop these by leaving the original `[label](file://…)`
    // intact, so the UI render path mustn't try to render them as
    // clickable HTTPS links.
    unsharedFiles = extractUnsharedFileLinks(displayText);
    // Strip them from the displayed text so Streamdown doesn't
    // double-render them as clickable `<a href="file://…">` anchors.
    // The unshared chip already conveys the same file to the user.
    if (unsharedFiles.length > 0) {
      displayText = displayText.replace(
        new RegExp(FILE_URI_LINK_RE.source, "g"),
        "",
      ).replace(new RegExp(TILDE_LINK_RE.source, "g"), "");
    }
    // Extract signed `/files/d/{jwt}` download links for rendering as
    // attachment pills below the message text.  The in-text links are
    // kept intact (dual representation: clickable link in prose + pill
    // below). For portable tilde links, match the displayed filename when
    // a signed link for the same name is also present. Preserve file://
    // URL semantics.
    sharedFiles = extractSharedFileLinks(displayText);
    const sharedUrls = new Set(sharedFiles.map((f) => f.url));
    const sharedNames = new Set(sharedFiles.map((f) => f.filename));
    unsharedFiles = unsharedFiles.filter(
      (f) => !sharedUrls.has(f.url) && !(f.url?.startsWith("~") && sharedNames.has(f.filename)),
    );
  }

  // Optimistic file parts take precedence (they have blob/data URLs with
  // previews).  For historical messages partFiles is empty, so tagFiles
  // (from the <uploaded_files> block) are used.
  let files: RenderFilePart[] =
    partFiles.length > 0 ? partFiles : tagFiles;

  // Append shared file-link pills (assistant messages only).  These are
  // downloadable signed URLs — the in-text link stays as a clickable
  // Markdown link, and the pill provides a second visual affordance.
  if (sharedFiles.length > 0) {
    files = files.concat(sharedFiles);
  }

  // Append any defensive unshared chips. De-duplication isn't critical
  // here because `extractUnsharedFileLinks` already de-dupes by URL,
  // and `files` comes from a different code path so URL overlap is
  // unlikely (shared files are already filtered against unshared URLs).
  if (unsharedFiles.length > 0) {
    files = files.concat(unsharedFiles);
  }

  const item: RenderItem = {
    kind: "message",
    id: msg.id,
    role: msg.role === "user" ? "user" : "assistant",
    text: displayText,
    ...(msg.toolGroupBoundaryId
      ? { toolGroupBoundaryId: msg.toolGroupBoundaryId }
      : {}),
    files,
    voiceTranscript,
    selectedSkills,
    userMeta,
    ...(msg.role === "user" && msg.metadata?.scheduledRun
      ? { scheduledRun: {
          ...msg.metadata.scheduledRun,
          firedAt: String(msg.metadata.createdAt ?? ""),
        } }
      : {}),
    flashMode: msg.metadata?.flashMode === true,
    ...(msg.metadata?.source === "composer_action"
      && typeof msg.metadata.action_label === "string"
      && typeof msg.metadata.task_id === "string"
      ? { composerAction: {
        label: msg.metadata.action_label,
        taskId: msg.metadata.task_id,
        ...(typeof msg.metadata.action_id === "string" ? { actionId: msg.metadata.action_id } : {}),
      } }
      : {}),
  };
  messageItemCache.set(msg, item);
  return item;
}

/**
 * Build a lookup of completed tool calls for the client-side pairing pass.
 *
 * For each (runId, toolName) pair we store completion timestamps. A start row
 * is hidden when a completion exists at a strictly later timestamp, matching
 * the backend's ordering semantics.
 *
 * Rows without a parseable createdAt are left renderable: without a reliable
 * ordering, suppressing a start row would be unsafe.
 */
function buildCompletedToolIndex(
  messages: AgnoChatMessage[],
): Map<string, number[]> {
  const index = new Map<string, number[]>();
  for (const msg of messages) {
    if (msg.role !== "tool") continue;
    const rawStatus = msg.metadata?.status;
    if (rawStatus !== "complete" && rawStatus !== "error") continue;
    const runId = msg.metadata?.runId as string | undefined;
    const toolName = msg.metadata?.toolName as string | undefined;
    if (!runId || !toolName) continue;
    const timestamp = Date.parse(String(msg.metadata?.createdAt ?? ""));
    if (!Number.isFinite(timestamp)) continue;

    const key = runId + "|" + toolName;
    const completions = index.get(key);
    if (completions) completions.push(timestamp);
    else index.set(key, [timestamp]);
  }
  return index;
}

/**
 * Map conversation messages to render items.
 *
 * Tool-call pairing
 * -----------------
 * The backend GET /api/sessions/:id/messages endpoint filters out redundant
 * tool "start" rows when a matching "complete"/"error" row exists, using a
 * correlated EXISTS subquery at query time.  This works for historical
 * fetches where both rows are already in the DB.
 *
 * However, during a **live run** a race condition occurs:
 *
 *   1. ``persist_tool_call_start`` writes the start row.
 *   2. The UI fetches messages — the start row is returned because no
 *      complete sibling exists yet.
 *   3. The UI caches the start row in the message store (keyed by id).
 *   4. The tool completes → a *separate* complete row (different id) is
 *      written.
 *   5. Later fetches (``?after=<cursor>``) only return messages newer than
 *      the cursor — the already-cached start row is never re-fetched or
 *      re-filtered.
 *   6. The complete row arrives as a new entry → both rows are in the store
 *      → both render as badges.
 *
 * ``mergeMessages`` upserts by ``id``, and start/complete rows have
 * different UUIDs, so the store never replaces the stale start row.
 *
 * This function applies a defense-in-depth pairing pass: a tool start row is
 * suppressed when a complete/error row with the same run ID and tool name
 * exists at a later timestamp. A lone start row remains visible as a spinning
 * badge.
 *
 * The ``status`` arg is used to append synthetic trailing items (the
 * standalone "Thinking…" and "Resuming…" bubbles) so they render as part of
 * the virtualized data array instead of being siblings outside the list.
 */
export function buildRenderItems(
  messages: AgnoChatMessage[],
  status: import("ai").ChatStatus,
  options: { incompleteEnd?: boolean } = {},
): RenderItem[] {
  let items: RenderItem[] = [];
  const completedTools = buildCompletedToolIndex(messages);
  // RunRequirement.id is stable across Agno continuations, SSE delivery, and
  // persisted marker rows. Track the IDs already represented by an earlier
  // pause in the same run so cumulative legacy rows and an optimistic +
  // canonical copy of the same pause cannot render duplicate approvals.
  const seenHitlRequirementIds = new Map<string, Set<string>>();

  for (const msg of messages) {
    if (msg.role === "tool") {
      const rawName = (msg.metadata?.toolName as string | undefined) ?? "unknown";
      const name = rawName ? rawName.slice(0, 256) : "unknown";
      const toolStatus = dbStatusToBadgeStatus(msg.metadata?.status);

      // Suppress a cached start row when its terminal sibling has since
      // arrived. This handles live-streaming rows that the backend cannot
      // retroactively remove from the client message cache.
      if (toolStatus === "start") {
        const runId = msg.metadata?.runId as string | undefined;
        if (runId && rawName !== "unknown") {
          const completions = completedTools.get(runId + "|" + rawName);
          if (completions) {
            const startTimestamp = Date.parse(
              String(msg.metadata?.createdAt ?? ""),
            );
            if (
              Number.isFinite(startTimestamp) &&
              completions.some((timestamp) => timestamp > startTimestamp)
            ) {
              continue;
            }
          }
        }
      }

      const isLive = msg.metadata?.toolDetailSource === "live";
      const liveDetails = isLive
        ? mergeLiveToolDetails(undefined, msg.metadata?.liveToolDetails)
        : undefined;
      const historicalSessionId = msg.metadata?.sessionId;
      const createdAt = msg.metadata?.createdAt;
      const hasCanonicalIdentity =
        typeof historicalSessionId === "string" &&
        historicalSessionId.length > 0 &&
        typeof createdAt === "string" &&
        Number.isFinite(Date.parse(createdAt));
      const source: ToolDetailSource = isLive
        ? {
            kind: "live",
            key: `live:${msg.id}`,
            details: liveDetails!,
          }
        : hasCanonicalIdentity
          ? {
              kind: "history",
              sessionId: historicalSessionId,
              messageId: msg.id,
            }
          : { kind: "unavailable", key: `unavailable:${msg.id}` };

      items.push({ kind: "tool", id: msg.id, name, status: toolStatus, source,
        workflowTaskId: msg.metadata?.workflowTaskId });
      continue;
    }

    // HITL: if the message has hitlPaused metadata, emit a hitl render item
    // instead of a regular message. The UI renders an ApprovalCard for these.
    // The `resolved` flag distinguishes an active (interactive) approval
    // prompt from a historical one that was already approved/rejected. Older
    // server rows may contain Agno's cumulative requirement history; project
    // each stable requirement id onto the first marker that introduced it.
    if (msg.metadata?.hitlPaused) {
      const runId = msg.metadata.runId as string;
      const requirements = Array.isArray(msg.metadata.requirements)
        ? (msg.metadata.requirements as RunRequirementInfo[])
        : [];
      let projectedRequirements = requirements;
      if (runId) {
        let seen = seenHitlRequirementIds.get(runId);
        if (!seen) {
          seen = new Set<string>();
          seenHitlRequirementIds.set(runId, seen);
        }
        projectedRequirements = requirements.filter((requirement) => {
          const requirementId = requirement?.id;
          // Legacy/malformed rows without a stable requirement id cannot be
          // safely correlated, so preserve them instead of hiding an action.
          if (typeof requirementId !== "string" || requirementId.length === 0) {
            return true;
          }
          if (seen.has(requirementId)) return false;
          seen.add(requirementId);
          return true;
        });
      }

      // A non-empty marker whose requirements were all seen is a duplicate
      // delivery of an already-rendered approval. Empty legacy markers remain
      // visible to preserve the existing fallback behavior.
      if (requirements.length > 0 && projectedRequirements.length === 0) {
        continue;
      }
      items.push({
        kind: "hitl",
        id: msg.id,
        runId,
        requirements: projectedRequirements,
        resolved: (msg.metadata.hitlResolved as boolean) ?? false,
        recoveryError: typeof msg.metadata.hitlRecoveryError === "string" ? msg.metadata.hitlRecoveryError : undefined,
      });
      continue;
    }

    // Compaction: if the message is a system-role compaction marker, emit
    // a compaction render item (dashed divider).  These rows have empty
    // content by design — the UI renders a divider, not a text bubble.
    if (msg.role === "system" && msg.metadata?.phase === "compaction") {
      items.push({
        kind: "compaction",
        id: msg.id,
        tokensBefore: (msg.metadata.tokens_before as number) ?? 0,
        summaryLength: (msg.metadata.summary_length as number) ?? 0,
      });
      continue;
    }

    items.push(messageToRenderItem(msg));
  }

  // ── Suppress whitespace-only assistant bubbles ──────────────────────────
  // `use-agno-chat.ts` mints a fresh `assistant` placeholder on every
  // `<<TOOL_CALL|start|...>>` and `<<TOOL_CALL|end|...>>` marker, so a
  // stream with N tool boundaries produces N+1 assistant placeholders
  // separating 4 user-visible text spans.  When two tool markers are
  // adjacent (no text between them — common for sequential tool calls
  // like access_info + ls × 3), the placeholders between them accumulate
  // nothing but the `\n` separator that wraps the marker, so they would
  // render as empty assistant bubbles ("Thinking..." spinners inside
  // them since their text is "").
  //
  // Drop any whitespace-only assistant item that is NOT the last item
  // in the array.  The last item MUST stay alive — during
  // SSE submitted/streaming, the renderer shows the "Thinking..." spinner
  // inside the last assistant bubble (see main.tsx itemContent branch),
  // and the standalone `__thinking__` bubble only appears when there is
  // no assistant item at all.  Removing the last entry would leave the
  // spinner without a host.
  //
  // User messages are never whitespace-only treated this way: a user
  // message with pure whitespace content still represents intent (even
  // if degenerate) and the renderer can't safely suppress it.
  // Tool / thinking / resuming items are also preserved as-is.
  if (items.length > 1) {
    const lastIdx = items.length - 1;
    items = items.filter((it, i) => {
      if (i === lastIdx) return true;
      if (it.kind !== "message" || it.role !== "assistant") return true;
      return it.text.trim() !== "";
    });
  }

  // ── Synthetic trailing bubbles ──────────────────────────────────────────
  // "Thinking…" spinner: shown as a separate bubble when a run is in-flight
  // but the current turn has not produced an assistant bubble yet. Covers:
  //   1. SSE send: status "submitted" before the POST returns (no assistant
  //      placeholder exists yet).
  //   2. Polling: entering a session with an already-running turn — status
  //      is "streaming" but no assistant message has been persisted yet, so
  //      there is no placeholder to host the in-bubble spinner.
  //   3. HITL resume: a session whose last DB message is a HITL-paused
  //      marker (role "assistant" but rendered as kind "hitl") was resumed
  //      elsewhere and is now streaming — no new assistant text bubble has
  //      been persisted yet, so the in-bubble spinner has no host.
  // Once an assistant message arrives it becomes the last entry, hiding this
  // standalone bubble; the spinner then renders inside that bubble (in the
  // itemContent renderer below) until text streams in.
  //
  // IMPORTANT: check the last RENDER ITEM, not the last raw message.  A
  // HITL-paused marker has role "assistant" in the message store but renders
  // as kind "hitl" (an ApprovalCard), not as an assistant text bubble.
  // Checking `messages[].role !== "assistant"` would see the HITL marker's
  // role and skip the __thinking__ bubble — leaving the user with no loading
  // indicator while the resumed run streams in the background.  The last
  // render item must be a kind "message" with role "assistant" to host the
  // in-bubble spinner; anything else (hitl, compaction, tool, thinking,
  // message+user) requires the standalone bubble.
  const lastItem = items.length > 0 ? items[items.length - 1] : null;
  if (
    (status === "submitted" || status === "streaming") &&
    !(lastItem?.kind === "message" && lastItem.role === "assistant")
  ) {
    items.push({ kind: "thinking", id: "__thinking__" });
  }

  // "Resuming…" bubble: shown when a stream was interrupted by a network
  // error and the resume polling path is trying to pick it up.
  if (
    status === "error" &&
    messages.some((m) => m.role === "assistant" && m.streamingError)
  ) {
    items.push({ kind: "resuming", id: "__resuming__" });
  }

  // ── Tool-call compaction: group contiguous tool rows into one item ─────
  // Streaming markers and persisted history both arrive here as the same
  // sequence of assistant and tool rows. Keep iterative assistant narration
  // inside the collapsible, but lift final assistant text and block-level
  // Markdown as visible prose.
  //
  // Rules:
  //   • The final assistant text segment and block-level assistant Markdown
  //     break a tool run. Plain prose and inline Markdown between calls
  //     remain inside the disclosure.
  //   • A final, one-call run immediately after lifted assistant prose stays a
  //     standalone ToolCallBadge instead of creating a second disclosure.
  //   • User, compaction, HITL, resuming, and the standalone thinking bubble
  //     end the surrounding tool segment.
  items = groupRenderItems(items, status, options);

  return items;
}

// ── Grouping helpers for tool-call compaction ───────────────────────────────

/** Whether an item can share a compacted row with adjacent tool calls. */
function isToolGroupScaffolding(
  item: RenderItem,
  isFinalAssistantText: boolean,
): boolean {
  if (item.kind === "tool") return true;
  if (item.kind === "thinking" && item.id !== "__thinking__") return true;
  if (item.kind === "message" && item.role === "assistant") {
    return !isFinalAssistantText && !hasBlockLevelMarkdown(item.text);
  }
  return false;
}

/** Whether an item is a "group boundary" that stops absorption. */
function isGroupBoundary(item: RenderItem): boolean {
  if (item.kind === "message" && item.role === "user") return true;
  if (item.kind === "task-status-event") return true;
  if (item.kind === "subagent-completion-event") return true;
  if (item.kind === "task-completion-events") return true;
  if (item.kind === "hitl") return true;
  if (item.kind === "compaction") return true;
  if (item.kind === "resuming") return true;
  if (item.kind === "thinking" && item.id === "__thinking__") return true;
  return false;
}

/**
 * For every index, record whether a tool appears at or after that point
 * before the next hard boundary. A one-call run after prose is only left
 * standalone when this lookup says it is the final tool run in its turn.
 */
function toolAtOrAfterBeforeBoundary(
  items: RenderItem[],
): boolean[] {
  const hasToolAtOrAfter = new Array<boolean>(items.length + 1).fill(false);
  let hasTool = false;

  for (let index = items.length - 1; index >= 0; index -= 1) {
    const item = items[index];
    if (isGroupBoundary(item)) {
      hasTool = false;
      continue;
    }
    if (item.kind === "tool") hasTool = true;
    hasToolAtOrAfter[index] = hasTool;
  }

  return hasToolAtOrAfter;
}

function isSubstantiveAssistantMessage(
  item: RenderItem,
): item is Extract<RenderItem, { kind: "message" }> & { role: "assistant" } {
  return (
    item.kind === "message" &&
    item.role === "assistant" &&
    item.text.trim() !== ""
  );
}

function isWhitespaceOnlyAssistantMessage(item: RenderItem): boolean {
  return (
    item.kind === "message"
    && item.role === "assistant"
    && item.text.trim() === ""
  );
}

/**
 * Whether an assistant message is the final text segment of its tool run.
 *
 * A text segment followed by another tool is intermediate narration, even if
 * it is the latest assistant text seen so far. A substantive assistant
 * message directly before the end of the item list or a hard group boundary
 * is the completed response and must remain visible outside the disclosure.
 */
function isFinalAssistantTextInRun(
  items: RenderItem[],
  index: number,
  incompleteEnd: boolean,
): boolean {
  const item = items[index];
  return (
    isSubstantiveAssistantMessage(item)
    && (
      (index === items.length - 1 && !incompleteEnd)
      || (index < items.length - 1 && isGroupBoundary(items[index + 1]))
    )
  );
}

/**
 * Group consecutive tool/scaffolding items into a single `tool-group` item.
 *
 * The function processes the list in linear time. Intermediate assistant
 * narration without block-level Markdown is absorbed with adjacent tool calls.
 * Final assistant text and block-level assistant text flush the run and
 * remain visible between disclosures.
 *
 * `isStreaming` is true only for the final group in the render list when the
 * run is still in-flight (status submitted/streaming). If the list ends with
 * a standalone `__thinking__` synthetic bubble, that bubble is considered
 * after the group, so the active group is still marked streaming.
 */
export function groupRenderItems(
  items: RenderItem[],
  status: import("ai").ChatStatus,
  options: { incompleteEnd?: boolean } = {},
): RenderItem[] {
  if (items.length === 0) return items;
  const isStreaming = status === "submitted" || status === "streaming";
  const hasToolAtOrAfter = toolAtOrAfterBeforeBoundary(items);

  // A trailing whitespace-only assistant row is the in-bubble streaming
  // spinner host. Keep it outside the group even though whitespace itself is
  // never a visible response boundary.
  let lastMeaningfulIdx = items.length - 1;
  if (
    lastMeaningfulIdx >= 0
    && items[lastMeaningfulIdx].kind === "thinking"
    && items[lastMeaningfulIdx].id === "__thinking__"
  ) {
    lastMeaningfulIdx -= 1;
  }

  const out: RenderItem[] = [];
  let toolRun: RenderItem[] = [];
  let toolRunFollowsAssistantText = false;
  // Use the segment boundary rather than the last tool message as the group
  // identity. Tool rows can be replaced in place when a start row receives
  // its terminal sibling, and the last tool naturally changes on every new
  // call. A stable identity lets lifted disclosure state survive those
  // updates and virtualized remounts.
  let segmentKey = "root";
  let groupOrdinal = 0;

  const flushGroup = (nextIndex: number) => {
    if (toolRun.length === 0) return;
    const toolCount = toolRun.filter((i) => i.kind === "tool").length;
    if (toolCount === 0) {
      // No actual tools in this run — just empty scaffolding, keep it as-is.
      out.push(...toolRun);
      toolRun = [];
      toolRunFollowsAssistantText = false;
      return;
    }

    // A lone trailing call such as add_memory after an otherwise complete
    // response is clearer as the existing standalone badge than as a second
    // one-call disclosure. It remains a group when another later call means
    // this run is one side of a text-separated split.
    if (
      toolCount === 1 &&
      toolRunFollowsAssistantText &&
      !hasToolAtOrAfter[nextIndex]
    ) {
      out.push(...toolRun);
      toolRun = [];
      toolRunFollowsAssistantText = false;
      return;
    }

    const lastTool = toolRun
      .slice()
      .reverse()
      .find((i): i is Extract<RenderItem, { kind: "tool" }> => i.kind === "tool")!;

    const group: RenderItem = {
      kind: "tool-group",
      id: `tool-group:${segmentKey}:${groupOrdinal}`,
      items: toolRun,
      toolCount,
      isStreaming: false,
      lastToolName: lastTool.name,
      lastToolStatus: lastTool.status,
    };
    groupOrdinal += 1;
    out.push(group);
    toolRun = [];
    toolRunFollowsAssistantText = false;
  };

  for (let index = 0; index < items.length; index += 1) {
    const item = items[index];
    const isWhitespaceHost =
      index === lastMeaningfulIdx && isWhitespaceOnlyAssistantMessage(item);
    const isFinalAssistantText = isFinalAssistantTextInRun(
      items,
      index,
      options.incompleteEnd === true,
    );
    if (
      isToolGroupScaffolding(item, isFinalAssistantText)
      && !isWhitespaceHost
    ) {
      if (toolRun.length === 0) {
        const previous = out[out.length - 1];
        toolRunFollowsAssistantText =
          previous !== undefined && isSubstantiveAssistantMessage(previous);
      }
      toolRun.push(item);
    } else {
      flushGroup(index);
      if (isGroupBoundary(item)) {
        segmentKey = item.kind === "message" && item.role === "user"
          ? item.toolGroupBoundaryId ?? item.id
          : item.id;
        groupOrdinal = 0;
      }
      out.push(item);
    }
  }

  flushGroup(items.length);

  // Determine which group is the active streaming group. A group is active
  // if it is the last non-thinking item and the run is in-flight.
  if (isStreaming) {
    // Find the last item that is not the standalone __thinking__ bubble.
    let lastMeaningfulIdx = out.length - 1;
    if (
      lastMeaningfulIdx >= 0 &&
      out[lastMeaningfulIdx].kind === "thinking" &&
      out[lastMeaningfulIdx].id === "__thinking__"
    ) {
      lastMeaningfulIdx -= 1;
    }
    if (
      lastMeaningfulIdx >= 0 &&
      out[lastMeaningfulIdx].kind === "tool-group"
    ) {
      const group = out[lastMeaningfulIdx];
      if (group.kind === "tool-group") {
        out[lastMeaningfulIdx] = { ...group, isStreaming: true };
      }
    }
  }

  return out;
}

// ── Thread anchor attachment ───────────────────────────────────────────────
//
// After buildRenderItems produces the base array, attachThreadAnchors inserts
// "thread-group" render items after message items that have known thread
// child sessions. This lets the conversation view show thread indicators
// inline in a single virtualized row.
// ────────────────────────────────────────────────────────────────────────────

/**
 * Insert thread indicator render items after their anchor messages.
 *
 * For each anchor reference, finds the matching `"message"` render item by
 * `sourceMessageId` and inserts one `"thread-group"` item immediately after
 * it.
 * If the anchor kind is `"block"` with a `sourceBlockIndex`, the indicator
 * is still placed after the message (block-level granularity is a future
 * enhancement; V1 attaches at message level).
 *
 * Multiple anchors on the same message are grouped into that item in reverse
 * order of `last_active_at` (most recent first).
 */
export function attachThreadAnchors(
  items: RenderItem[],
  anchors: ThreadAnchorRef[],
): RenderItem[] {
  if (anchors.length === 0) return items;

  // Sort anchors by last_active_at descending (most recent first) so
  // multiple threads on the same message appear newest-first.
  const sorted = [...anchors].sort(
    (a, b) =>
      new Date(b.last_active_at).getTime() -
      new Date(a.last_active_at).getTime(),
  );

  // Build a map: sourceMessageId → ThreadAnchorRef[]
  const anchorMap = new Map<string, ThreadAnchorRef[]>();
  for (const ref of sorted) {
    const existing = anchorMap.get(ref.source_message_id);
    if (existing) existing.push(ref);
    else anchorMap.set(ref.source_message_id, [ref]);
  }

  const result: RenderItem[] = [];
  for (const item of items) {
    result.push(item);

    // Only attach after message items (not tool/thinking/compaction/etc.)
    if (item.kind !== "message") continue;

    const refs = anchorMap.get(item.id);
    if (!refs) continue;

    result.push({
      kind: "thread-group",
      id: `thread-group:${item.id}`,
      sourceMessageId: item.id,
      threads: refs.map((ref) => ({
        childSessionId: ref.child_session_id,
        sourceAnchorKind: ref.source_anchor_kind as "message" | "block",
        sourceBlockIndex: ref.source_block_index ?? null,
        threadTitle: ref.title ?? null,
      })),
    });
  }

  return result;
}
